import { GroupBase, Props as ReactSelectProps } from 'react-select'
import SearchableSelect, {
	SearchableSelectCreatableProps,
	SearchableSelectOption,
	SearchableSelectWidthProps,
	defaultStyles,
} from './SearchableSelect'
import { getColor } from '../../../lib/tailwindTheme'

const textColor = '#fff'
// Same shade as the main page background, so the outline reads as a subtle frame on the darker bar.
const frameColor = getColor('charcoal', '500') as string
const indicatorHoverBackground = getColor('charcoal', '600') as string
const placeholderColor = getColor('charcoal', '100') as string

/**
 * Layers the dark-surface look over the canonical light defaults, key by key. Only the closed
 * control is restyled: the open menu and options deliberately keep the canonical light template.
 */
const darkOverrides: Record<string, (base: any, state: any) => any> = {
	control: (base, state) => ({
		...base,
		backgroundColor: 'transparent',
		// The focus ring from the canonical defaults is kept for keyboard users.
		borderColor: state.isFocused ? base.borderColor : frameColor,
		boxShadow: state.isFocused ? base.boxShadow : 'none',
		'&:hover': { borderColor: state.isFocused ? base.borderColor : frameColor },
	}),
	input: (base) => ({ ...base, color: textColor }),
	singleValue: (base) => ({ ...base, color: textColor }),
	placeholder: (base) => ({ ...base, color: placeholderColor }),
	indicatorsContainer: (base) => ({
		...base,
		'&::before': { ...base['&::before'], backgroundColor: frameColor },
	}),
	dropdownIndicator: (base) => ({
		...base,
		color: textColor,
		'&:hover': { color: textColor, backgroundColor: indicatorHoverBackground },
	}),
	clearIndicator: (base) => ({
		...base,
		color: textColor,
		'&:hover': { color: textColor, backgroundColor: indicatorHoverBackground },
	}),
}

type DarkSearchableSelectProps<
	Option extends SearchableSelectOption<unknown>,
	IsMulti extends boolean,
	Group extends GroupBase<Option>
> = ReactSelectProps<Option, IsMulti, Group> & SearchableSelectWidthProps & SearchableSelectCreatableProps<Option>

/**
 * Canonical closed-control style for dark surfaces (top bar, sidebar): transparent with a permanent
 * dark-grey rounded outline and arrow divider, white text. The open menu is the standard
 * SearchableSelect menu. Use SearchableSelect on light surfaces.
 */
function DarkSearchableSelect<
	Option extends SearchableSelectOption<unknown>,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
>(props: DarkSearchableSelectProps<Option, IsMulti, Group>) {
	const callerStyles = (props.styles ?? {}) as Record<string, any>
	const styles: Record<string, any> = { ...callerStyles }
	Object.keys(darkOverrides).forEach((key) => {
		const baseFn = (defaultStyles as Record<string, any>)[key]
		styles[key] = (base: any, state: any) => {
			let result = baseFn ? baseFn(base, state) : base
			result = darkOverrides[key](result, state)
			return typeof callerStyles[key] === 'function' ? callerStyles[key](result, state) : result
		}
	})

	return <SearchableSelect<Option, IsMulti, Group> {...props} styles={styles} />
}

export default DarkSearchableSelect
